import { useState, useRef } from 'react';
import { useContent } from '@/hooks/useContent';
import { Link } from 'react-router-dom';

export default function HeroSection() {
  const { t } = useContent('home');
  const { t: tc } = useContent('common');
  const { t: imgT } = useContent('images');
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const formRef = useRef<HTMLFormElement>(null);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypot = formData.get('website_alt') as string;
    if (honeypot && honeypot.trim() !== '') {
      setFormStatus('success');
      return;
    }
    formData.delete('website_alt');
    const params = new URLSearchParams();
    formData.forEach((value, key) => {
      params.append(key, value.toString());
    });
    setFormStatus('loading');
    setFormError('');
    try {
      const response = await fetch('https://readdy.ai/api/form/d9603trjjt5fme3c53cg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string } } = {};
      try { parsed = JSON.parse(responseText); } catch { parsed = {}; }
      const serverMsg = parsed?.meta?.message || parsed?.meta?.detail || responseText;
      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else if (typeof serverMsg === 'string' && serverMsg.toLowerCase().includes('spam')) {
        setFormStatus('error');
        setFormError(tc('common_error_spam'));
      } else {
        setFormStatus('error');
        setFormError(serverMsg || tc('common_error'));
      }
    } catch {
      setFormStatus('error');
      setFormError(tc('common_error_connection'));
    }
  };

  return (
    <section className="relative w-full min-h-screen flex items-center overflow-hidden">
      <div className="absolute inset-0 overflow-hidden">
        <video
          autoPlay
          muted
          loop
          playsInline
          className="absolute inset-0 w-full h-full object-cover object-center opacity-25"
          src={imgT('hero_video')}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/25 via-black/15 to-black/10 z-[2]"></div>
      </div>

      <div className="relative z-10 w-full max-w-7xl mx-auto px-4 md:px-6 pt-32 pb-16">
        <div className="flex flex-col lg:flex-row items-center gap-10 lg:gap-16">
          <div className="flex-1 w-full">
            <div className="flex flex-wrap items-center gap-3 mb-5">
              <span className="inline-flex items-center gap-2 bg-primary-500/15 border border-primary-500/40 text-primary-500 text-xs font-semibold uppercase tracking-widest px-4 py-1.5 rounded-full">
                <i className="ri-home-smile-line w-4 h-4 flex items-center justify-center"></i>
                {t('hero_badge')}
              </span>
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-white leading-tight mb-6">
              {t('hero_title')}
            </h1>
            <p className="text-white/80 text-lg leading-relaxed mb-6 max-w-xl">
              {t('hero_desc')}
            </p>
            <div className="flex flex-wrap gap-4 mb-10">
              <div className="flex items-center gap-3 bg-white/10 border border-primary-500/30 rounded-xl px-5 py-3 hover:border-primary-500/60 transition-all cursor-default">
                <i className="ri-map-pin-line text-primary-500 text-xl w-6 h-6 flex items-center justify-center"></i>
                <div>
                  <p className="text-white text-xs font-bold tracking-wider">{t('hero_location')}</p>
                  <p className="text-primary-500 text-[10px] font-semibold">{t('hero_local')}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 bg-white/10 border border-primary-500/30 rounded-xl px-5 py-3 hover:border-primary-500/60 transition-all cursor-default">
                <i className="ri-time-line text-primary-500 text-xl w-6 h-6 flex items-center justify-center"></i>
                <div>
                  <p className="text-white text-xs font-bold tracking-wider">{t('hero_flexible')}</p>
                  <p className="text-primary-500 text-[10px] font-semibold">{t('hero_flexible_sub')}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 bg-white/10 border border-primary-500/30 rounded-xl px-5 py-3 hover:border-primary-500/60 transition-all cursor-default">
                <i className="ri-price-tag-3-line text-primary-500 text-xl w-6 h-6 flex items-center justify-center"></i>
                <div>
                  <p className="text-white text-xs font-bold tracking-wider">{t('hero_free_quote')}</p>
                  <p className="text-primary-500 text-[10px] font-semibold">{t('hero_free_quote_sub')}</p>
                </div>
              </div>
            </div>
            <div className="flex flex-wrap gap-8">
              <div>
                <p className="text-2xl font-extrabold text-primary-500">15 años +</p>
                <p className="text-white/70 text-sm mt-0.5">{t('hero_stat_years')}</p>
              </div>
              <div>
                <p className="text-3xl font-extrabold text-primary-500"></p>
                <p className="text-white/70 text-sm mt-0.5">{t('hero_stat_satisfaction')}</p>
              </div>
              <div>
                <p className="text-2xl font-extrabold text-primary-500 whitespace-nowrap">{tc('common_free_estimate')}</p>
                <p className="text-white/70 text-sm mt-0.5">{t('hero_stat_free')}</p>
              </div>
              <div>
                <p className="text-2xl font-extrabold text-primary-500">{tc('common_local')}</p>
                <p className="text-white/70 text-sm mt-0.5">{t('hero_stat_local')}</p>
              </div>
            </div>
          </div>

          <div className="w-full lg:w-[420px] shrink-0">
            <div className="bg-black/50 backdrop-blur-sm rounded-2xl p-8">
              <div className="mb-6 pb-5 border-b border-foreground-50/10">
                <h2 className="text-foreground-50 text-2xl font-extrabold leading-snug">{t('hero_form_title')}</h2>
                <p className="text-foreground-400 text-sm mt-1">{t('hero_form_subtitle')}</p>
              </div>

              {formStatus === 'success' ? (
                <div className="flex flex-col items-center justify-center py-8 gap-4">
                  <div className="w-16 h-16 flex items-center justify-center bg-primary-500/20 rounded-full">
                    <i className="ri-check-line text-primary-500 text-3xl w-8 h-8 flex items-center justify-center"></i>
                  </div>
                  <p className="text-foreground-50 text-center font-bold text-lg">{tc('common_success')}</p>
                  <p className="text-foreground-400 text-center text-sm">{tc('common_success_contact')}</p>
                  <button
                    onClick={() => setFormStatus('idle')}
                    className="whitespace-nowrap bg-primary-500 hover:bg-primary-600 text-white font-bold py-2 px-6 rounded-lg text-sm cursor-pointer transition-all"
                  >
                    {tc('common_send_another')}
                  </button>
                </div>
              ) : (
                <form data-readdy-form="true" id="hero-contact-form" ref={formRef} className="flex flex-col gap-4" onSubmit={handleSubmit}>
                  <input type="text" name="website_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly className="honeypot-field" />
                  <div>
                    <label className="block text-foreground-50 text-sm font-semibold mb-1.5">{tc('common_full_name')}</label>
                    <input
                      placeholder={t('hero_placeholder_name')}
                      className="w-full bg-white/5 border border-white/10 text-foreground-50 placeholder-foreground-400 text-sm rounded-lg px-4 py-3 outline-none focus:border-primary-500 focus:bg-white/10 transition-all"
                      required
                      name="fullName"
                    />
                  </div>
                  <div>
                    <label className="block text-foreground-50 text-sm font-semibold mb-1.5">{tc('common_email')}</label>
                    <input
                      placeholder={t('hero_placeholder_email')}
                      className="w-full bg-white/5 border border-white/10 text-foreground-50 placeholder-foreground-400 text-sm rounded-lg px-4 py-3 outline-none focus:border-primary-500 focus:bg-white/10 transition-all"
                      required
                      type="email"
                      name="email"
                    />
                  </div>
                  <div>
                    <label className="block text-foreground-50 text-sm font-semibold mb-1.5">{tc('common_phone')}</label>
                    <input
                      placeholder={t('hero_placeholder_phone')}
                      className="w-full bg-white/5 border border-white/10 text-foreground-50 placeholder-foreground-400 text-sm rounded-lg px-4 py-3 outline-none focus:border-primary-500 focus:bg-white/10 transition-all"
                      required
                      type="tel"
                      name="phone"
                    />
                  </div>
                  <div>
                    <label className="block text-foreground-50 text-sm font-semibold mb-1.5">{tc('common_service_needed')}</label>
                    <select
                      name="interest"
                      className="w-full bg-white/10 border border-foreground-50/20 text-foreground-50 text-sm rounded-lg px-4 py-3 outline-none focus:border-primary-500 transition-all cursor-pointer"
                      required
                      defaultValue=""
                    >
                      <option value="" disabled>{tc('common_select_service')}</option>
                      <option value="Spray Foam Insulation">{tc('spray_foam')}</option>
                      <option value="Batt Insulation">{tc('batt_insulation')}</option>
                      <option value="Roll Insulation">{tc('roll_insulation')}</option>
                      <option value="Cellulose Insulation">{tc('cellulose_insulation')}</option>
                      <option value="Blown-In Insulation">{tc('blown_in_insulation')}</option>
                      <option value="Attic Insulation">{tc('attic_insulation')}</option>
                      <option value="Wall Insulation">{tc('wall_insulation')}</option>
                      <option value="Crawl Space Insulation">{tc('crawl_space_insulation')}</option>
                      <option value="Metal Building Insulation">{tc('metal_building_insulation')}</option>
                      <option value="General Consultation">{tc('general_consultation')}</option>
                    </select>
                  </div>
                  {formStatus === 'error' && (
                    <p className="text-red-400 text-xs">{formError}</p>
                  )}
                  <button
                    type="submit"
                    disabled={formStatus === 'loading'}
                    className="whitespace-nowrap w-full bg-primary-500 hover:bg-primary-600 disabled:opacity-60 text-white font-bold py-4 rounded-lg text-sm flex items-center justify-center gap-2 transition-all duration-200 cursor-pointer mt-1"
                  >
                    {formStatus === 'loading' ? (
                      <><i className="ri-loader-4-line animate-spin w-5 h-5 flex items-center justify-center"></i>{tc('common_sending')}</>
                    ) : (
                      <><i className="ri-send-plane-line text-base w-5 h-5 flex items-center justify-center"></i>{t('hero_submit')}</>
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-20 flex flex-col items-center gap-1 opacity-50">
        <span className="text-white text-xs tracking-widest uppercase">{tc('common_scroll')}</span>
        <i className="ri-arrow-down-line text-white text-lg w-5 h-5 flex items-center justify-center animate-bounce"></i>
      </div>
    </section>
  );
}